import { Table } from "antd";
import { useSelector } from "react-redux";
import { useEffect, useRef, useState } from "react";
import styles from "../../../styles/Components/tables/portfolioTable.module.sass";
import { capitalizeString } from "../../../utility/formatter-helper";
import customStyles from "../../../styles/Components/new-insight/signals-section/InsightSignalsTable.module.css";
import ServiceActivationModal from "../../../shared/service-activation/ServiceActivationModal";
import LiveSignalCell from "./signals-table/LiveSignalCell";
import SignalCell from "./signals-table/SignalCell";
import AssetCell from "./signals-table/AssetCell";
import PlaceOrderButton from "./PlaceOrderButton";
import useIsMobile from "../../../hooks/useIsMobile";
import { FaChevronLeft, FaInfoCircle } from "react-icons/fa";
import { FaChevronRight } from "react-icons/fa6";
import { liveSuggestionFor, normalizeLiveTimeframe } from "../../../redux/live-suggestion/liveSuggestionSlice";

function headerMeta(info) {
  if (!info || typeof info !== "object") return {};
  const category = String(info.category || "").trim();
  const subcategory = String(info.subcategory || "").trim();
  const name = String(info.name || "").trim();
  return { category, subcategory, name };
}

function headerFallback(column) {
  const raw = String(column || "")
    .replace(/[_-]+/g, " ")
    .replace(/\s+/g, " ")
    .trim();
  if (!raw) return "Signal";
  return capitalizeString(raw);
}

function InsightSignalsTable() {
  const {
    newInsightState: {
      signalStatus,
      signalData,
      signalError,
      signalTableColumns,
      systemStrategies,
      insightMarketType,
    },
    liveSuggestion: { suggestions, liveTimeframe },
  } = useSelector((state) => state);

  const isMobile = useIsMobile();

  const isCryptoMarket = (insightMarketType || "crypto") === "crypto";
  const [showServiceActivationModal, setShowServiceActivationModal] =
    useState(false);

  const [showSlowLoading, setShowSlowLoading] = useState(false);

  const slowLoadingMessages = [
    "Crunching the latest market data for you…",
    "Scanning signals across all your assets — hang tight.",
    "Calibrating our models to find the best entries.",
    "Analyzing trends so you don't have to.",
    "Warming up the engines — your signals are on the way.",
    "Cross-checking multiple sources for accuracy.",
    "Loading real-time insights, almost there.",
    "Polishing your custom strategy outputs.",
  ];

  function pickNextSlowLoadingMessage(currentIndex) {
    if (!slowLoadingMessages.length) return { message: "", index: -1 };
    if (slowLoadingMessages.length === 1)
      return { message: slowLoadingMessages[0], index: 0 };
    let nextIndex = Math.floor(Math.random() * slowLoadingMessages.length);
    if (nextIndex === currentIndex) {
      nextIndex = (nextIndex + 1) % slowLoadingMessages.length;
    }
    return { message: slowLoadingMessages[nextIndex], index: nextIndex };
  }

  const [slowLoadingTip, setSlowLoadingTip] = useState({
    message: slowLoadingMessages[0] || "",
    index: 0,
  });
  const [messageVisible, setMessageVisible] = useState(false);
  const slowLoadingMessageTimerRef = useRef(null);
  const slowLoadingIntervalRef = useRef(null);

  const tableWrapperRef = useRef(null);
  const slowLoadingTimerRef = useRef(null);

  const [tableData, setTableData] = useState([]);

  const scrollTable = (e, direction) => {
    e.stopPropagation();
    const wrapper = tableWrapperRef.current;
    if (!wrapper) return;

    const scrollAmount = 300;
    const scrollContainer = wrapper.querySelector(".ant-table-body");

    if (!scrollContainer) return;

    scrollContainer.scrollBy({
      left: direction === "left" ? -scrollAmount : scrollAmount,
      behavior: "smooth",
    });
  };

  const placeOrderColumn = {
    title: <span style={{ color: "#555" }}>Place Order</span>,
    key: "placeOrder",
    dataIndex: "placeOrder",
    align: "center",
    fixed: isMobile ? false : "left",
    width: 120,
    render: (_, record) => <PlaceOrderButton coin={record?.coin} />,
  };

  const newColumn = {
    title: (
      <span style={{ color: "#555" }}>
        {isCryptoMarket ? `${normalizeLiveTimeframe(liveTimeframe)} Signal` : "15m Signal"}
      </span>
    ),
    key: "liveSignal",
    dataIndex: "liveSignal",
    align: "center",
    width: 200,
    fixed: isMobile ? false : "left",
    render: (_, record) => {
      return <LiveSignalCell coin={record.coin} />;
    },
  };

  let columns = signalTableColumns.map((column, i) => {
    const headerInfo = headerMeta(
      (signalData || []).find((row) => {
        const info = headerMeta(row?.[column]?.other_info);
        return info.category || info.subcategory || info.name;
      })?.[column]?.other_info,
    );
    const { subcategory, category, name } = headerInfo;
    const fallback = headerFallback(column);

    return {
      title: (
        <div style={{ color: "#555" }}>
          {fallback === "Coin" ? (
            "Asset"
          ) : (
            <div className={customStyles.tableHeader}>
              <span>{name || fallback}</span>
              {category || subcategory ? (
                <small>
                  {[category, subcategory].filter(Boolean).join(" · ")}
                </small>
              ) : null}
            </div>
          )}
        </div>
      ),
      key: column,
      dataIndex: column,
      align: "center",
      width: 200,
      fixed:
        capitalizeString(column) === "Coin" ? (isMobile ? false : "left") : "",
      render: (value) => {
        if (typeof value === "object" && value !== null) {
          return <SignalCell signal={value} />;
        }
        return <AssetCell coin={value} />;
      },
    };
  });

  // Insert the new column
  columns.splice(1, 0, placeOrderColumn, newColumn);

  useEffect(() => {
    if (!signalData.length || signalStatus === "loading") {
      setTableData([]);
      return;
    }
    if (signalStatus !== "idle") return;
    const transformedData = [...signalData]
      ?.sort((a, b) => {
        const aPrediction = liveSuggestionFor(
          suggestions,
          b.coin,
          liveTimeframe,
        )?.prediction;
        return aPrediction === "Hold" ? 1 : -1;
      })
      .map((signal) => {
        // Flatten the nested data structure for each signal
        const flattenedSignal = { coin: signal.coin, key: signal?.coin };
        signalTableColumns.forEach((column) => {
          const columnData = signal[column];

          // Check if columnData is an object (e.g., "Momentum - Awesome Oscillator")
          if (typeof columnData === "object" && columnData !== null) {
            const {
              suggested_amount_percentage: amountSuggestionPercentage,
              stop_loss_percent: stopLossPercentage,
              take_profit_percent: takeProfitPercentage,
              signal_date: date,
              other_info: { name, subcategory, category, description } = {},
              signal_description,
            } = columnData;

            flattenedSignal[column] = {
              suggestion: columnData.suggestion,
              strength: columnData["signal strength"],
              roi: columnData["recent ROI"],
              data: {
                amountSuggestionPercentage,
                stopLossPercentage,
                takeProfitPercentage,
              },
              coin: signal.coin,
              date,
              key: `${column}/${category}/${subcategory}/${name}`,
              liveSignal: signal.coin,
              name,
              subcategory,
              category,
              signal_description,
              description,
            };
          } else {
            flattenedSignal[column] = columnData || "-";
          }
        });
        return flattenedSignal;
      });
    setTableData(transformedData);
  }, [signalData, signalStatus, signalTableColumns, suggestions]);
  useEffect(() => {
    if (signalStatus === "error") {
      setTableData([]);
      columns = ["Signals"];
    }
  }, [signalStatus]);

  useEffect(() => {
    if (slowLoadingTimerRef.current) {
      clearTimeout(slowLoadingTimerRef.current);
      slowLoadingTimerRef.current = null;
    }
    if (slowLoadingIntervalRef.current) {
      clearInterval(slowLoadingIntervalRef.current);
      slowLoadingIntervalRef.current = null;
    }
    if (slowLoadingMessageTimerRef.current) {
      clearTimeout(slowLoadingMessageTimerRef.current);
      slowLoadingMessageTimerRef.current = null;
    }

    setShowSlowLoading(false);
    setMessageVisible(false);

    if (signalStatus === "loading") {
      slowLoadingTimerRef.current = setTimeout(() => {
        setShowSlowLoading(true);
        const first = pickNextSlowLoadingMessage(-1);
        setSlowLoadingTip(first);
        setMessageVisible(true);

        slowLoadingIntervalRef.current = setInterval(() => {
          setMessageVisible(false);
          slowLoadingMessageTimerRef.current = setTimeout(() => {
            setSlowLoadingTip((prev) => pickNextSlowLoadingMessage(prev.index));
            setMessageVisible(true);
          }, 340);
        }, 3800);
      }, 15000);
    }

    return () => {
      if (slowLoadingTimerRef.current) {
        clearTimeout(slowLoadingTimerRef.current);
      }
      if (slowLoadingIntervalRef.current) {
        clearInterval(slowLoadingIntervalRef.current);
      }
      if (slowLoadingMessageTimerRef.current) {
        clearTimeout(slowLoadingMessageTimerRef.current);
      }
    };
  }, [signalStatus]);

  return (
    <div className={customStyles.tableWrapper} ref={tableWrapperRef}>
      {columns?.length > 2 && tableData?.length > 0 && (
        <>
          <span
            className={`${customStyles.btn} ${customStyles.backBtn}`}
            onClick={(e) => scrollTable(e, "left")}
          >
            <FaChevronLeft size={14} />
          </span>

          <span
            className={`${customStyles.btn} ${customStyles.nextBtn}`}
            onClick={(e) => scrollTable(e, "right")}
          >
            <FaChevronRight size={14} />
          </span>
        </>
      )}
      {signalStatus === "error" && (
        <div
          style={{
            padding: "12px 16px",
            marginBottom: "14px",
            backgroundColor: "#FEF2F2",
            border: "1px solid #FECACA",
            borderLeft: "4px solid #d64545",
            borderRadius: "14px",
            fontFamily: "Poppins-Regular",
            fontSize: "14px",
            color: "#7F1D1D",
          }}
        >
          {typeof signalError === "string" && signalError.trim()
            ? signalError
            : "Signals took too long to load. Refresh to try again."}
        </div>
      )}
      {showSlowLoading && (
        <div className={customStyles.slowLoadingCard}>
          <FaInfoCircle
            size={18}
            color="#01B388"
            style={{ marginTop: "2px", flexShrink: 0 }}
            className={customStyles.slowLoadingIcon}
          />
          <div className={customStyles.slowLoadingBody}>
            <span className={customStyles.slowLoadingTitle}>
              Retrieving your signals
            </span>
            <div className={customStyles.slowLoadingMessageWrap}>
              <div
                className={`${customStyles.slowLoadingMessage} ${
                  messageVisible ? customStyles.slowLoadingMessageEntered : ""
                }`}
                key={slowLoadingTip.index}
              >
                {slowLoadingTip.message}
              </div>
            </div>
          </div>
        </div>
      )}
      <Table
        columns={columns}
        className={`${styles.table} ${customStyles.table}`}
        dataSource={tableData}
        scroll={{ x: "max-content", y: 700 }}
        pagination={false}
        bordered
        loading={signalStatus === "loading" || !systemStrategies.length}
      />

      {showServiceActivationModal && (
        <ServiceActivationModal
          service={"insai"}
          show={showServiceActivationModal}
          setShow={setShowServiceActivationModal}
        />
      )}
    </div>
  );
}

export default InsightSignalsTable;
